import { Injectable, inject, computed } from "@angular/core";
import { BehaviorSubject, Subject, Observable, of } from "rxjs";
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  take,
  map,
  filter,
} from "rxjs/operators";
import { DateRangeService } from "./date-range.service";
import { from } from "rxjs";
import {
  BackendApiService,
  apiErrorMessage,
} from "@app/common/services/backend-api.service";
import { GridItem } from "@app/common/components/grid-layout/grid-layout.component";
import dayjs from "dayjs/esm";
import { AuthService } from "@app/common/services/auth.service";
import { PortfolioStateService } from "@app/parse-analyze-track/services/portfolio-state.service";
import { DashboardDataService, DashboardView } from "./dashboard-data.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  GridItemType,
  DashboardItemType,
} from "@app/common/types/platform.types";
import { WritableSignal, signal } from "@angular/core";
import { ChartData } from "@app/common/charts/services/chart.service";
import { EChartsCoreOption } from "echarts/core";

export const USER_NOTES_ID = "userNotes";

export interface DashboardState {
  grid_layout: GridItem[];
  date_range: {
    startDate: string;
    endDate: string;
    preset?: string;
  };
  portfolio_id: number;
  metadata?: {
    last_view_id?: number;
    compareChartSymbols?: string[];
    loadedAt?: number; // Timestamp to force distinctUntilChanged to pass on fresh loads
  };
}

// @DASHBOARD_ITEM: Add new ChartType ID here (from dashboard.component.ts)
const SYMBOL_TRADE_ALLOCATION_PIE_CHART_ID = "symbolTradeAllocationPie";
const WIN_RATE_HORIZONTAL_BAR_CHART_ID = "winRateHorizontalBarChart";
const STOCK_OPTION_GAIN_BAR_CHART_ID = "stockOptionGainChart";
const STANDALONE_ECHART_TYPE = "standaloneEchart";

@Injectable({
  providedIn: "root",
})
export class DashboardStateService {
  private readonly SAVE_DELAY = 2000;
  private api = inject(BackendApiService);
  private dateRangeService = inject(DateRangeService);
  private authService = inject(AuthService);
  private portfolioState = inject(PortfolioStateService);
  private dashboardDataService = inject(DashboardDataService);
  private snackBar = inject(MatSnackBar);
  private destroy$ = new Subject<void>();

  // State management
  private stateSubject = new BehaviorSubject<DashboardState | null>(null);
  private saveStateSubject = new Subject<DashboardState>();
  private activeViewUpdateSubject = new Subject<{
    view: DashboardView;
    gridLayout: GridItem[];
  }>();

  // Observable for components to subscribe to
  readonly currentState$ = this.stateSubject.asObservable();

  // Add computed signal for selected portfolio ID
  private readonly selectedPortfolioId = computed(
    () => this.portfolioState.getSelected()?.id
  );

  // New map to store chart data signals
  private dataSignals = new Map<string, WritableSignal<any | null>>();

  constructor() {
    // Initialize chart data signals for all known chart types
    Object.values(GridItemType).forEach((chartId) => {
      if (typeof chartId === "string") {
        // Enum values can be numbers if not specified
        this.dataSignals.set(chartId, signal(null));
      }
    });
    // Add signals for string-based chart IDs from DashboardComponent
    this.dataSignals.set(SYMBOL_TRADE_ALLOCATION_PIE_CHART_ID, signal(null));
    this.dataSignals.set(WIN_RATE_HORIZONTAL_BAR_CHART_ID, signal(null));
    this.dataSignals.set(STOCK_OPTION_GAIN_BAR_CHART_ID, signal(null));
    this.dataSignals.set(USER_NOTES_ID, signal(null));
    // Note: STANDALONE_ECHART_TYPE is a type, not an ID, so we don't add it here unless it's used as an ID.

    // Setup state saving with debounce
    this.saveStateSubject
      .pipe(takeUntil(this.destroy$), debounceTime(this.SAVE_DELAY))
      .subscribe((state) => {
        this._saveState(state);
      });

    // Setup active view update with the same debounce
    this.activeViewUpdateSubject
      .pipe(takeUntil(this.destroy$), debounceTime(this.SAVE_DELAY))
      .subscribe(({ view, gridLayout }) => {
        this._updateActiveViewState(view, gridLayout);
      });
  }

  /**
   * Updates the data for a specific grid tile and logs the update.
   * @param id The unique identifier for the grid data.
   * @param data The new data for the grid tile.
   */
  updateGridData(id: string, data: ChartData | EChartsCoreOption | any): void {
    if (this.dataSignals.has(id)) {
      this.dataSignals.get(id)!.set(data);
      // console.log(`DashboardStateService: Updated data for ${id}`, data);
    } else {
      // If the chartId is not pre-initialized (e.g. dynamic or new), create a signal for it.
      const newSignal = signal(data);
      this.dataSignals.set(id, newSignal);
      // console.log(
      //   `DashboardStateService: Initialized and updated data for new id ${id}`,
      //   data
      // );
    }
  }

  /**
   * Retrieves the signal for a specific grid data.
   * @param id The unique identifier for the grid item.
   * @returns A signal containing the grid data, or undefined if not found.
   */
  getGridDataSignal(id: string): WritableSignal<any | null> | undefined {
    return this.dataSignals.get(id);
  }

  /**
   * Retrieves a snapshot of all current grid data.
   * @returns A map where keys are grid IDs and values are the current grid data.
   */
  getAllGridData(): Map<string, any | null> {
    const allData = new Map<string, any | null>();
    this.dataSignals.forEach((dataSignal, id) => {
      allData.set(id, dataSignal());
    });
    return allData;
  }

  /**
   * Updates the dashboard state and triggers a debounced save
   */
  updateState(gridLayout: GridItem[], activeView?: DashboardView) {
    const startDate = this.dateRangeService.startDate();
    const endDate = this.dateRangeService.endDate();
    const activePreset = this.dateRangeService.activePreset();
    const portfolioId = this.selectedPortfolioId();
    const currentState = this.stateSubject.getValue();

    if (!portfolioId) {
      console.error(
        "DashboardStateService: No portfolio selected. Skipping state update."
      );
      return;
    }

    const newState: DashboardState = {
      grid_layout: gridLayout,
      date_range: {
        startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : "",
        endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "",
        preset: activePreset || undefined, //i think we only want to change the preset if we create a new dashboard view
      },
      portfolio_id: portfolioId,
      metadata: currentState?.metadata, // Preserve metadata
    };

    this.stateSubject.next(newState);
    this.saveStateSubject.next(newState);

    // If there's an active view, also update that with the same debounce
    if (activeView) {
      this.activeViewUpdateSubject.next({ view: activeView, gridLayout });
    } else {
    }
  }

  /**
   * Gets the default grid layout for initial state
   */
  getDefaultGridItems(): GridItem[] {
    return [
      // Row 1: 8 + 4 = 12 columns
      {
        id: GridItemType.ProfitLossByUnderlying,
        cols: 8,
        rows: 2,
        title: "P/L by Underlying",
        type: DashboardItemType.Chart,
        chartConfig: {
          type: "bar",
          height: "400px",
          data: null, // Placeholder - will be overridden by component
        },
        enabled: true,
      },
      {
        id: GridItemType.TradeStats,
        cols: 4,
        rows: 2,
        title: "Trade Stats",
        type: DashboardItemType.TradeStats,
        enabled: true,
      },

      // Row 2: 6 + 6 = 12 columns
      {
        id: GridItemType.ProfitLossOverTime,
        cols: 6,
        rows: 2,
        title: "P/L Over Time",
        type: DashboardItemType.Chart,
        chartConfig: {
          type: "line",
          height: "400px",
          data: null, // Placeholder - will be overridden by component
        },
        enabled: true,
      },
      {
        id: GridItemType.ProfitLossByStrategy,
        cols: 6,
        rows: 2,
        title: "P/L by Strategy",
        type: DashboardItemType.Chart,
        chartConfig: {
          type: "bar",
          height: "400px",
          data: null, // Placeholder - will be overridden by component
        },
        enabled: true,
      },

      // Row 3: 4 + 4 + 4 = 12 columns
      {
        id: GridItemType.ProfitLossByTimePeriod,
        cols: 4,
        rows: 2,
        title: "P/L by Time Period",
        type: DashboardItemType.Chart,
        chartConfig: {
          type: "bar",
          height: "400px",
          data: null, // Placeholder - will be overridden by component
        },
        enabled: true,
      },
      {
        id: GridItemType.ProfitLossByExpiration,
        cols: 4,
        rows: 2,
        title: "P/L by Expiration",
        type: DashboardItemType.Chart,
        chartConfig: {
          type: "bar",
          height: "400px",
          data: null, // Placeholder - will be overridden by component
        },
        enabled: true,
      },
      {
        id: "symbolTradeAllocationPie",
        cols: 4,
        rows: 2,
        title: "Trade Frequency",
        type: DashboardItemType.Chart,
        chartConfig: {
          type: "pie",
          height: "400px",
          data: null, // Placeholder - will be overridden by component
        },
        enabled: true,
      },

      // Row 4: 8 + 4 = 12 columns (Timeline + Notes together)
      {
        id: GridItemType.UnderlyingTimeline,
        type: DashboardItemType.Chart,
        title: "Timeline",
        cols: 8,
        rows: 2,
      },
      {
        id: GridItemType.Comments,
        cols: 4,
        rows: 2,
        title: "Notes",
        type: DashboardItemType.Comments,
        enabled: true,
      },

      // Compare chart temporarily removed from the dashboard widget set.
      // Keep the implementation here commented out so it can be restored later.
      // {
      //   id: GridItemType.Compare,
      //   cols: 8,
      //   rows: 3,
      //   title: "Compare",
      //   type: DashboardItemType.Chart,
      //   chartConfig: {
      //     type: "line",
      //     height: "400px",
      //     data: null, // Placeholder - will be overridden by component
      //   },
      //   enabled: true,
      // },
      {
        id: GridItemType.Watchlist,
        cols: 4,
        rows: 3,
        title: "Watchlist",
        type: DashboardItemType.Watchlist,
        enabled: true,
      },

      // Row 6: 12 columns (full width)
      {
        id: "winRateHorizontalBarChart",
        cols: 12,
        rows: 2,
        title: "Trade Efficiency by Underlying",
        type: STANDALONE_ECHART_TYPE as any,
        enabled: true,
      },
      {
        id: "stockOptionGainChart",
        cols: 12,
        rows: 2,
        title: "Stock vs Options",
        type: STANDALONE_ECHART_TYPE as any,
        enabled: true,
      },
    ];
  }

  /**
   * Updates the active view state with debounce
   */
  private _updateActiveViewState(
    view: DashboardView,
    gridLayout: GridItem[]
  ): void {
    // Merge current grid_filters with compareChartSymbols
    const currentCompareSymbols = this.getCompareChartSymbols();
    const updatedGridFilters = {
      ...(view.gridFilters || {}),
      compareChartSymbols: currentCompareSymbols,
    };

    this.dashboardDataService
      .updateDashboardViewLayout({
        id: view.id,
        grid_layout: gridLayout,
        grid_filters: updatedGridFilters,
      })
      .subscribe({
        error: (error) => {
          console.error("Error updating dashboard view layout:", error);
          this.snackBar.open("Error updating dashboard view", "Close", {
            duration: 3000,
          });
        },
      });
  }

  /**
   * Updates the last selected view ID and saves the state
   */
  updateLastViewId(viewId: number) {
    const currentState = this.stateSubject.getValue();
    if (!currentState) {
      return;
    }

    const newState: DashboardState = {
      ...currentState,
      metadata: {
        ...currentState.metadata,
        last_view_id: viewId,
      },
    };

    this.stateSubject.next(newState);
    this.saveStateSubject.next(newState);
  }

  /**
   * Gets the last selected view ID
   */
  getLastViewId(): number | undefined {
    const lastViewId = this.stateSubject.getValue()?.metadata?.last_view_id;
    return lastViewId;
  }

  /**
   * Updates the compare chart symbols in memory only (no immediate save)
   * Saving will happen through the normal debounced grid state update
   */
  updateCompareChartSymbols(symbols: string[]) {
    const currentState = this.stateSubject.getValue();
    const selectedPortfolioId = this.selectedPortfolioId();

    if (!currentState || !selectedPortfolioId) {
      return;
    }

    // Validate that we're updating for the correct portfolio
    if (currentState.portfolio_id !== selectedPortfolioId) {
      return;
    }

    const newState: DashboardState = {
      ...currentState,
      metadata: {
        ...currentState.metadata,
        compareChartSymbols: symbols,
      },
    };

    this.stateSubject.next(newState);
  }

  /**
   * Gets the current compare chart symbols
   */
  getCompareChartSymbols(): string[] {
    const currentState = this.stateSubject.getValue();
    const selectedPortfolioId = this.selectedPortfolioId();

    if (!currentState || !selectedPortfolioId) {
      return [];
    }

    // Validate that we're getting symbols for the correct portfolio
    if (currentState.portfolio_id !== selectedPortfolioId) {
      console.warn(
        `DashboardStateService: Portfolio mismatch when getting compareChartSymbols. Current state: ${currentState.portfolio_id}, Selected: ${selectedPortfolioId}. Returning empty array to prevent cross-portfolio contamination.`
      );
      return [];
    }

    const symbols = currentState.metadata?.compareChartSymbols;
    return symbols || [];
  }

  /**
   * Sets the compare chart symbols (replaces existing)
   */
  setCompareChartSymbols(symbols: string[]) {
    this.updateCompareChartSymbols(symbols);
  }

  /**
   * Adds a symbol to the compare chart
   */
  addCompareChartSymbol(symbol: string) {
    const currentSymbols = this.getCompareChartSymbols();
    if (!currentSymbols.includes(symbol)) {
      this.updateCompareChartSymbols([...currentSymbols, symbol]);
    }
  }

  /**
   * Removes a symbol from the compare chart
   */
  removeCompareChartSymbol(symbol: string) {
    const currentSymbols = this.getCompareChartSymbols();
    const updatedSymbols = currentSymbols.filter((s) => s !== symbol);
    this.updateCompareChartSymbols(updatedSymbols);
  }

  /**
   * Persists the state to the database
   */
  private _saveState(state: DashboardState): void {
    this.authService.currentUser$
      .pipe(
        take(1),
        map((user) => user?.uid ?? null),
        filter((userId) => !!userId)
      )
      .subscribe((userId) => {
        if (!userId) {
          throw new Error("No user ID available for saving dashboard state");
        }

        // Create a copy of grid layout that includes metadata
        const gridLayoutWithMetadata = {
          items: state.grid_layout,
          metadata: state.metadata || {},
        };

        // PUT /api/v1/portfolios/:id/dashboard-state (the caller's own row).
        from(
          this.api.client.PUT("/api/v1/portfolios/{id}/dashboard-state", {
            params: { path: { id: state.portfolio_id } },
            body: {
              gridLayout: gridLayoutWithMetadata,
              dateRange: state.date_range,
            },
          })
        ).subscribe({
          next: ({ error }) => {
            if (error) {
              console.error(
                "Error saving dashboard state:",
                apiErrorMessage(error, "Dashboard state was not saved.")
              );
            }
          },
          error: (error) => {
            console.error("Error saving dashboard state:", error);
          },
        });
      });
  }

  /**
   * Force saves the current state immediately
   * Useful for component cleanup
   */
  saveState(): void {
    const currentState = this.stateSubject.getValue();
    if (currentState) {
      this._saveState(currentState);
    }
  }

  /**
   * Loads the saved state from the database for a specific portfolio
   */
  loadSavedState(portfolioId: number) {
    // Clear any existing compareChartSymbols from previous portfolio to prevent contamination
    const currentState = this.stateSubject.getValue();
    if (currentState) {
      this.stateSubject.next({
        ...currentState,
        metadata: {
          ...currentState.metadata,
          compareChartSymbols: [],
        },
      });
    }

    this.authService.currentUser$
      .pipe(
        take(1),
        map((user) => user?.uid ?? null)
      )
      .subscribe((userId) => {
        if (!userId) {
          console.error("No user ID available for loading dashboard state");
          return;
        }

        // GET /api/v1/portfolios/:id/dashboard-state; 404 = nothing saved yet.
        from(
          this.api.client.GET("/api/v1/portfolios/{id}/dashboard-state", {
            params: { path: { id: portfolioId } },
          })
        )
          .pipe(
            map(({ data, error, response }) => {
              if (response?.status === 404) return null;
              if (error || !data) {
                throw new Error(
                  apiErrorMessage(error, "Dashboard state load failed.")
                );
              }
              return data as {
                gridLayout: any;
                dateRange: any;
                portfolioId: number;
              };
            })
          )
          .subscribe({
            next: (savedState) => {
              // If no saved state exists, create initial state using updateState()
              if (!savedState) {
                // Clear any existing state before initializing to prevent metadata carryover
                this.stateSubject.next(null);
                this.updateState(this.getDefaultGridItems());
                return;
              }
              // Extract metadata from grid_layout if it exists, but clear compareChartSymbols
              // to prevent cross-portfolio contamination
              const savedMetadata =
                savedState.gridLayout?.metadata || undefined;
              const metadata = savedMetadata
                ? {
                    ...savedMetadata,
                    compareChartSymbols:
                      savedMetadata.compareChartSymbols || [], // Use saved symbols for this portfolio
                  }
                : undefined;

              // Check if a preset was saved
              const dateRange = savedState.dateRange as {
                startDate: string;
                endDate: string;
                preset?: string;
              };

              // Only apply saved preset if there's no current active date range
              // This preserves the global date state when navigating between pages
              const currentDateRange = this.dateRangeService.dateRange();
              const hasCurrentDateRange =
                currentDateRange.start && currentDateRange.end;

              if (dateRange.preset && !hasCurrentDateRange) {
                this.dateRangeService.setActivePreset(dateRange.preset as any);
                // The component using the preset will set the actual date range
              }

              // console.log(
              //   "default state dashboard state loading",
              //   savedState.id + " - id,",
              //   savedState.portfolioId + " - pid",
              //   savedState.gridLayout.items.map((item: GridItem) => item.title)
              // );
              this.stateSubject.next({
                grid_layout: (savedState.gridLayout?.items ||
                  savedState.gridLayout) as GridItem[],
                date_range: dateRange,
                portfolio_id: savedState.portfolioId,
                metadata: {
                  ...metadata,
                  loadedAt: Date.now(),
                },
              });
            },
            error: (error) => {
              console.error("Error loading dashboard state:", error);
            },
          });
      });
  }
}
